/**
 * Voice Enrollment Skip Management Service - Server-Side Version
 * Handles progressive skip system with 24-hour intervals using backend API
 */
class VoiceSkipService {
  constructor() {
    this.baseURL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
  }

  /**
   * Get skip status for a user from server
   * @param {string} token - User authentication token
   * @returns {Object} Skip status information
   */
  async getSkipStatus(token) {
    if (!token) {
      return {
        canSkip: false,
        skipsRemaining: 0,
        isMandatory: true,
        message: 'Authentication required'
      };
    }

    try {
      const response = await fetch(`${this.baseURL}/api/voice/skip-status?token=${encodeURIComponent(token)}`);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      
      const data = await response.json();
      
      return {
        canSkip: data.can_skip,
        skipsRemaining: data.skips_remaining,
        isMandatory: data.is_mandatory,
        isInCooldown: data.is_in_cooldown,
        hoursRemaining: data.hours_remaining,
        message: data.message
      };
    } catch (error) {
      console.error('Error fetching skip status:', error);
      return {
        canSkip: false,
        skipsRemaining: 0,
        isMandatory: true,
        isInCooldown: false,
        message: 'Failed to load skip status'
      };
    }
  }

  /**
   * Process a skip action on server
   * @param {string} token - User authentication token
   * @returns {Object} Result of skip action
   */
  async processSkip(token) {
    if (!token) {
      return { success: false, message: 'Authentication required' };
    }

    try {
      const response = await fetch(`${this.baseURL}/api/voice/process-skip`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token })
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      
      const data = await response.json();
      
      return {
        success: data.success,
        skipsRemaining: data.skips_remaining,
        isFinalSkip: data.is_final_skip,
        message: data.message
      };
    } catch (error) {
      console.error('Error processing skip:', error);
      return {
        success: false,
        message: 'Failed to process skip'
      };
    }
  }

  /**
   * Mark voice profile as completed on server
   * @param {string} token - User authentication token
   * @returns {Object} Result of completion
   */
  async completeVoiceProfile(token) {
    if (!token) {
      return { success: false, message: 'Authentication required' };
    }

    try {
      const response = await fetch(`${this.baseURL}/api/voice/complete-profile`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token })
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      
      const data = await response.json();
      
      return {
        success: data.success,
        message: data.message
      };
    } catch (error) {
      console.error('Error completing voice profile:', error);
      return {
        success: false,
        message: 'Failed to complete voice profile'
      };
    }
  }

  /**
   * Check if user's voice profile is completed (server-side check)
   * @param {string} token - User authentication token
   * @returns {boolean} Whether voice profile is completed
   */
  async isVoiceProfileCompleted(token) {
    if (!token) return false;

    try {
      const status = await this.getSkipStatus(token);
      // If the message indicates the profile is completed, return true
      return status.message === 'Voice profile already completed';
    } catch (error) {
      console.error('Error checking voice profile completion status:', error);
      return false;
    }
  }

  /**
   * Check if user should see voice enrollment on app start
   * @param {string} token - User authentication token
   * @returns {boolean} Whether to show voice enrollment
   */
  async shouldShowVoiceEnrollment(token) {
    if (!token) return false;

    try {
      const status = await this.getSkipStatus(token);
      
      // Don't show if voice profile is completed
      if (status.message === 'Voice profile already completed') {
        return false;
      }
      
      // Don't show if in cooldown period
      if (status.isInCooldown) {
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error checking if should show voice enrollment:', error);
      return false;
    }
  }

  // Legacy methods for backward compatibility - now use server-side data
  getSkipData(userId) {
    console.warn('getSkipData is deprecated - use getSkipStatus with token instead');
    return {
      skipsUsed: 0,
      skipsRemaining: 3,
      lastSkipTime: null,
      createdAt: new Date().toISOString()
    };
  }

  saveSkipData(userId, data) {
    console.warn('saveSkipData is deprecated - skip data is now stored server-side');
  }

  resetSkipData(userId) {
    console.warn('resetSkipData is deprecated - skip data is now managed server-side');
  }
}

export const voiceSkipService = new VoiceSkipService();